import React, { useState } from 'react';
import ProductDetailModal from '../Product/ProductDetailModal';
import { logInfo } from '../../config_and_helpers/logger';
import './AiChatStyle.css';

const LOG_CONTEXT = 'AiChatProductCitationsTable';

/**
 * Display text for a product citation catalog number.
 *
 * @param {Object} citation
 * @returns {string}
 */
const getProductCatNum = citation =>
  citation?.cat_num?.trim() ||
  (citation?.product_id ? `#${citation.product_id}` : '—');

/**
 * Display text for a product citation name.
 *
 * @param {Object} citation
 * @returns {string}
 */
const getProductName = citation => citation?.name?.trim() || '—';

/**
 * Tabular list of product citations with stable Cat# and Name columns.
 * Labels are plain table cells (never tied to ProductDetailModal's trigger text).
 * Row click opens the product detail modal.
 *
 * @param {Object} props
 * @param {Array<Object>} props.citations
 * @returns {JSX.Element|null}
 */
const AiChatProductCitationsTable = ({ citations = [] }) => {
  const [openProductId, setOpenProductId] = useState(null);

  const productCitations = citations.filter(
    citation =>
      (citation?.entity_type || 'product') === 'product' && citation?.product_id
  );

  if (productCitations.length === 0) return null;

  const openCitation = productCitations.find(
    citation => String(citation.product_id) === String(openProductId)
  );

  const handleRowActivate = citation => {
    logInfo(LOG_CONTEXT, 'opening product citation', {
      product_id: citation.product_id,
      cat_num: citation.cat_num,
    });
    setOpenProductId(citation.product_id);
  };

  return (
    <>
      <table className="ai-chat-citations-table">
        <thead>
          <tr>
            <th scope="col">Cat#</th>
            <th scope="col">Name</th>
          </tr>
        </thead>
        <tbody>
          {productCitations.map((citation, idx) => {
            const catNum = getProductCatNum(citation);
            const name = getProductName(citation);
            const rowKey = `product-${citation.product_id}-${idx}`;
            const isOpen =
              openProductId != null &&
              String(openProductId) === String(citation.product_id);

            return (
              <tr
                key={rowKey}
                className={
                  isOpen
                    ? 'ai-chat-citations-table__row ai-chat-citations-table__row--active'
                    : 'ai-chat-citations-table__row'
                }
                tabIndex={0}
                role="button"
                aria-label={`Open product ${catNum} ${name}`}
                onClick={() => handleRowActivate(citation)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleRowActivate(citation);
                  }
                }}
              >
                <td className="ai-chat-citations-table__primary" title={catNum}>
                  {catNum}
                </td>
                <td className="ai-chat-citations-table__secondary" title={name}>
                  {name}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {openCitation && (
        <div className="ai-chat-citations-table__modal-host" aria-hidden="true">
          <ProductDetailModal
            key={openCitation.product_id}
            productId={openCitation.product_id}
            hideTrigger
            show
            onHide={() => {
              logInfo(LOG_CONTEXT, 'closing product citation modal', {
                product_id: openCitation.product_id,
              });
              setOpenProductId(null);
            }}
          />
        </div>
      )}
    </>
  );
};

export default AiChatProductCitationsTable;
